import moment from 'moment';
import { ReactElement } from 'react';
import {
  GraphPointStyles,
  GraphSummaryData,
  MultiDeviceSummaryType,
  ParametersType,
  PlotDataType,
  PlotTransformedDataType,
  UserConfig
} from '../constants/telosair-interfaces';
import 'moment-timezone';
import { changeZeroValuesToNullForNumber } from './globalHelper';
import Deque from '../classes/deque';

/**
 * Function to calculate quartiles.
 * @param data - Data.
 * @param q - Desired quantile.
 * @returns - Quartile data.
 */
export function quartile(data: number[], q: number): number {
  const sortedData = data.slice().sort((a, b) => a - b);
  const pos = (sortedData.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedData[base + 1] !== undefined) {
    return sortedData[base] + rest * (sortedData[base + 1] - sortedData[base]);
  }
  return sortedData[base];
}

/**
 * Generates data for box and whisker.
 * @param data - Dataponits.
 * @returns - Generated data.
 */
export function generateBoxPlotData(data: number[][]): number[] {
  // Sort the data in ascending order
  const sharedData = data.map((item) => item[1]);
  sharedData.sort((a, b) => a - b);

  // Calculate quartiles and median
  const Q1 = quartile(sharedData, 0.25);
  const median = quartile(sharedData, 0.5);
  const Q3 = quartile(sharedData, 0.75);

  // Calculate interquartile range (IQR)
  // const IQR = Q3 - Q1;

  // Determine whisker range (usually 1.5 times IQR)
  // const lowerWhisker = Q1 - 1.5 * IQR;
  // const upperWhisker = Q3 + 1.5 * IQR;

  // Find outliers
  // const outliers = data.filter((value) => value < lowerWhisker || value > upperWhisker);

  return [
    parseFloat(Math.min(...sharedData).toFixed(2)), // minimum value
    parseFloat(Q1.toFixed(2)), // quartile one
    parseFloat(Q3.toFixed(2)), // quartile three
    parseFloat(Math.max(...sharedData).toFixed(2)), // maximum value
    parseFloat(median.toFixed(2)) // median value
  ];
}

/**
 * Function to get x axis labels.
 * @param timestamp - Timestamp of the axis label.
 * @param userConfigData - User Config Data.
 * @returns - XAxis label.
 */
export const getFormattedXAxisLabel = (timestamp: number, userConfigData: UserConfig): string => {
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return moment(timestamp).tz(timeZone).format('HH:mm');
};

/**
 * Function to find closest small value in an array.
 * @param array - Array to be used.
 * @param value - Value to find.
 * @returns - Closest number.
 */
export function closestSmallerValue(array: number[], value: number): number {
  let result: number = 0;
  let lastDelta: number = 0;
  // eslint-disable-next-line func-names
  array.some((item) => {
    const delta = Math.abs(value - item);
    if (delta >= lastDelta) {
      return true;
    }
    result = item;
    lastDelta = delta;
    return false;
  });

  return result;
}

/**
 * Function to find closest larger value in an array.
 * @param array - Array to be used.
 * @param value - Value to find.
 * @returns - Closest number.
 */
export function closestLargerValue(array: number[], value: number): number {
  let result: number = 0;
  let lastDelta: number = 0;
  // eslint-disable-next-line func-names
  array.some((item) => {
    const delta = Math.abs(value - item);
    if (delta > lastDelta) {
      return true;
    }
    result = item;
    lastDelta = delta;
    return false;
  });

  return result;
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function cleanGraphData(input: any): any {
  const cleanedData: any = {
    data: [],
    xAxis: []
  };

  // Clean xAxis
  if (input.xAxis.length > 1) {
    cleanedData.xAxis = [input.xAxis[0]];
  } else {
    cleanedData.xAxis = input.xAxis;
  }

  // Clean data
  cleanedData.data = input.data.map((item: any) => {
    if (item.data.length > 1) {
      return {
        ...item,
        data: [item.data[0]]
      };
    }
    return item;
  });

  return cleanedData;
}

/**
 * Generates unique combinations of devices and parameters.
 * @param data - Data.
 * @returns - Combinations.
 */
export function generateUniqueDeviceParameterCombinations(
  data: Record<string, string[]>
): string[] {
  const { parameter, device } = data;
  const combinations = new Set<string>();
  if (!parameter || parameter?.length === 0) {
    return [...device];
  }
  if (!device || device?.length === 0) {
    return [...parameter];
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const d of device) {
    // eslint-disable-next-line no-restricted-syntax
    for (const p of parameter) {
      combinations.add(`${d}:${p}`);
    }
  }

  return Array.from(combinations);
}
type DataPoint = any[];
/**
 * Function to slice data for thr graph.
 * @param data - Graph data.
 * @param maxPoints - Max points.
 * @returns - Updated data.
 */
export const removeExcessItemsFromArray = (data: any[], maxPoints: number): any[] => {
  if (data.length <= maxPoints) {
    return data; // Return data as is if it's within the maxPoints limit
  }

  // Ensure the first and last data points are included
  const firstPoint: DataPoint = data[0];
  const lastPoint: DataPoint = data[data.length - 1];

  // Determine the number of points to randomly select
  const numToRandomlySelect: number = maxPoints - 2; // Exclude the first and last points

  // Extract the middle section of the data
  const middleData: DataPoint[] = data.slice(1, -1);

  // Randomly sample from the middle data
  const sampledMiddleData: DataPoint[] = [];
  const indices: Set<number> = new Set();

  while (sampledMiddleData.length < numToRandomlySelect) {
    const randomIndex: number = Math.floor(Math.random() * middleData.length);
    if (!indices.has(randomIndex)) {
      indices.add(randomIndex);
      sampledMiddleData.push(middleData[randomIndex]);
    }
  }

  // Combine the first, sampled middle data, and last points
  const combinedData: DataPoint[] = [firstPoint, ...sampledMiddleData, lastPoint];

  // Sort the combined data by time (first element of each array)
  return combinedData.sort((a, b) => a[0] - b[0]);
};

/**
 * Transforms data for plot.
 * @param data - Data.
 * @returns - Transformed dara.
 */
export function transformDataForPlotByDevice(
  data: MultiDeviceSummaryType
): PlotTransformedDataType {
  const result: PlotTransformedDataType = {};
  Object.keys(data).forEach((device) => {
    const deviceData = data[device];
    const timestampMap: Record<number, PlotDataType> = {} as PlotDataType;
    deviceData.forEach((measurement) => {
      const { name, data: measurementData } = measurement;
      measurementData.forEach(([timestamp, value]) => {
        if (!timestampMap[timestamp]) {
          timestampMap[timestamp] = { timestamp, parameters: {} as any };
        }
        timestampMap[timestamp].parameters[name] = changeZeroValuesToNullForNumber(value);
      });
    });
    result[device] = Object.values(timestampMap);
  });

  return result;
}

/**
 * Transforms data for plot.
 * @param data - Data.
 * @returns - Transformed dara.
 */
export function transformDataForPlotByParameter(data: GraphSummaryData[]): PlotDataType[] {
  let result: PlotDataType[] = [];
  const timestampMap: Record<number, PlotDataType> = {} as PlotDataType;
  data.forEach((measurement) => {
    const { name, data: measurementData } = measurement;
    measurementData.forEach(([timestamp, value]) => {
      if (!timestampMap[timestamp]) {
        timestampMap[timestamp] = { timestamp, parameters: {} as any };
      }
      timestampMap[timestamp].parameters[name] = changeZeroValuesToNullForNumber(value);
    });
  });
  result = Object.values(timestampMap);
  return result;
}

/**
 * Transforms data for plot.
 * @param data - Data.
 * @returns - Transformed dara.
 */
export function transformDataForPlotByQueues(data: Record<ParametersType, Deque>): PlotDataType[] {
  let result: PlotDataType[] = [];
  const timestampMap: Record<number, PlotDataType> = {} as PlotDataType;

  const ParameterData = Object.entries(data) as [ParametersType, Deque][];
  ParameterData.forEach(([key, item]: [ParametersType, Deque]) => {
    const measurementData = item.toArray();
    measurementData.forEach(([timestamp, value]) => {
      if (!timestampMap[timestamp]) {
        timestampMap[timestamp] = { timestamp, parameters: {} as any };
      }
      timestampMap[timestamp].parameters[key] = changeZeroValuesToNullForNumber(value);
    });
  });
  result = Object.values(timestampMap);
  return result;
}

/**
 * Transforms data for plot.
 * @param data - Data.
 * @returns - Transformed dara.
 */
export function transformDataForPlot(data: GraphSummaryData[]): PlotDataType[] {
  let result: PlotDataType[] = [];
  const timestampMap: Record<number, PlotDataType> = {} as PlotDataType;
  data.forEach((measurement) => {
    const { name, data: measurementData } = measurement;
    measurementData.forEach(([timestamp, value]) => {
      if (!timestampMap[timestamp]) {
        timestampMap[timestamp] = { timestamp, parameters: {} as any };
      }
      timestampMap[timestamp].parameters[name] = changeZeroValuesToNullForNumber(value);
    });
  });
  result = Object.values(timestampMap);
  return result;
}
/**
 * Function to show icon as per pointstyle.
 * @param pointStyle - Point style.
 * @returns ReactElement.
 */
export const getIconByPointStyle = (pointStyle: GraphPointStyles): ReactElement => {
  switch (pointStyle) {
    case 'circle':
      return <span className="material-icons text-sm">circle</span>;
    case 'cross':
      return <span className="material-icons rotate-45 text-sm">close</span>;
    case 'crossRot':
      return <span className="material-icons text-sm">close</span>;
    case 'rect':
      return <span className="material-icons text-sm">square</span>;
    case 'rectRot':
      return <span className="material-icons rotate-45 text-sm">square</span>;
    case 'rectRounded':
      return <span className="material-icons-round text-sm">square</span>;
    case 'star':
      return <span className="material-icons-outlined text-sm">ac_unit</span>;
    case 'triangle':
      return <span className="material-symbols-outlined text-sm">change_history</span>;
    default:
      return <span className="material-icons text-sm">circle</span>;
  }
};

/**
 * Creates an object from an array where each key is an element from the array and each value is a color.
 * @param arr - The array of strings to be used as keys.
 * @param localParameterList - Local list with colors.
 * @returns An object where the keys are elements from the array and the values are colors.
 */
export function createColorObject(
  arr: ParametersType[],
  localParameterList: any[]
): { [key: string]: string } {
  const colorObject: { [key: string]: string } = {};

  arr.forEach((item) => {
    const parameter = localParameterList.find((localParameter) => localParameter.value === item);
    if (parameter) {
      colorObject[item] = parameter.color;
    }
  });

  return colorObject;
}
